import { Dictionary } from 'lodash';
import React, { FC, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectFinancialsContractType } from 'api/project';
import { StatusFieldOption } from 'api/tasks';
import { ProjectFinancialsContext } from 'contexts/project-financials';
import { useProjectFinancialsTableColumnDefinitions } from '../../hooks/column-definitions';
import { useProjectFinancialsTable } from '../../hooks/table';
import { ProjectFinancialsComponentType } from '../../types';
import {
  getFilteredZoomLevels,
  projectFinancialsGroupByMap,
} from '../../utils/common';
import {
  DataCardTitleWrapper,
  ProjectFinancialsGroupByRenderer,
  ProjectFinancialsZoomLevelSwitcher,
  RowWrapper,
  VerticalRowWrapper,
} from '../common';
import { FinancialsDataCard } from '../data-card';
import { ProjectFinancialsCardEmptyState } from '../empty-state';
import { FinancialsTable } from '../table';

interface Props {
  taskStatusOptionsLookup: Dictionary<StatusFieldOption>;
}

export const ProjectFinancialsTables: FC<Props> = ({
  taskStatusOptionsLookup,
}) => {
  const context = useContext(ProjectFinancialsContext);
  const {
    projectFinancials: {
      startDate,
      endDate,
      projectFinancialDetails: { financialContractType },
    },
    allowedZoomLevels,
    projectFinancialsVisibilityConfig: { profitabilityData },
  } = context;

  const {
    isBudgetTableLoading,
    isHoursTableLoading,
    budgetTableRange,
    hoursTableRange,
    budgetRowData,
    hoursRowData,
    budgetTableGroupBy,
    hoursTableGroupBy,
    budgetTableZoomLevel,
    hoursTableZoomLevel,
    onBudgetTableGroupByChange: onBudgetGroupByChange,
    onHoursTableGroupByChange: onHoursGroupByChange,
    onBudgetTableZoomLevelChange: onBudgetZoomLevelChange,
    onHoursTableZoomLevelChange: onHoursZoomLevelChange,
  } = useProjectFinancialsTable();

  const {
    budgetColumnDefs,
    hoursColumnDefs,
    budgetAutoGroupColumnDef,
    hoursAutoGroupColumnDef,
  } = useProjectFinancialsTableColumnDefinitions({
    budgetTableRange,
    hoursTableRange,
    budgetTableZoomLevel,
    hoursTableZoomLevel,
    budgetTableGroupBy,
    hoursTableGroupBy,
    taskStatusOptionsLookup,
  });

  const filteredBudgetZoomLevels = useMemo(
    () =>
      getFilteredZoomLevels(
        allowedZoomLevels[ProjectFinancialsComponentType.BudgetTable],
        startDate,
        endDate,
      ),
    [allowedZoomLevels, endDate, startDate],
  );

  const filteredHoursZoomLevels = useMemo(
    () =>
      getFilteredZoomLevels(
        allowedZoomLevels[ProjectFinancialsComponentType.HoursTable],
        startDate,
        endDate,
      ),
    [allowedZoomLevels, endDate, startDate],
  );

  return (
    <VerticalRowWrapper
      reverse={
        financialContractType === ProjectFinancialsContractType.NONBILLABLE
      }
    >
      <RowWrapper>
        <FinancialsDataCard
          primary={
            <>
              <DataCardTitleWrapper>
                <FormattedMessage
                  id={`project.financials.${financialContractType}.table.budget.title`}
                />
              </DataCardTitleWrapper>
              {budgetRowData != null && (
                <ProjectFinancialsGroupByRenderer
                  options={projectFinancialsGroupByMap}
                  selectedOption={budgetTableGroupBy}
                  onGroupByChange={onBudgetGroupByChange}
                />
              )}
            </>
          }
          content={
            budgetRowData == null ||
            (financialContractType ===
              ProjectFinancialsContractType.NONBILLABLE &&
              !profitabilityData) ? (
              <ProjectFinancialsCardEmptyState />
            ) : (
              <FinancialsTable
                isTableLoading={isBudgetTableLoading}
                rowData={budgetRowData}
                columnDefs={budgetColumnDefs}
                autoGroupColumnDef={budgetAutoGroupColumnDef}
              />
            )
          }
          secondary={
            <ProjectFinancialsZoomLevelSwitcher
              tabs={filteredBudgetZoomLevels}
              activeTab={budgetTableZoomLevel}
              onTabSwitch={onBudgetZoomLevelChange}
            />
          }
          central={<></>}
        />
      </RowWrapper>
      <RowWrapper>
        <FinancialsDataCard
          primary={
            <>
              <DataCardTitleWrapper>
                <FormattedMessage
                  id={`project.financials.${financialContractType}.table.hours.title`}
                />
              </DataCardTitleWrapper>
              {hoursRowData != null && (
                <ProjectFinancialsGroupByRenderer
                  options={projectFinancialsGroupByMap}
                  selectedOption={hoursTableGroupBy}
                  onGroupByChange={onHoursGroupByChange}
                />
              )}
            </>
          }
          content={
            hoursRowData == null ? (
              <ProjectFinancialsCardEmptyState />
            ) : (
              <FinancialsTable
                isTableLoading={isHoursTableLoading}
                rowData={hoursRowData}
                columnDefs={hoursColumnDefs}
                autoGroupColumnDef={hoursAutoGroupColumnDef}
              />
            )
          }
          secondary={
            <ProjectFinancialsZoomLevelSwitcher
              tabs={filteredHoursZoomLevels}
              activeTab={hoursTableZoomLevel}
              onTabSwitch={onHoursZoomLevelChange}
            />
          }
          central={<></>}
        />
      </RowWrapper>
    </VerticalRowWrapper>
  );
};
